export const getBookingStyles = () => `
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');

    :root {
        /* Force App Theme Color for UI consistency */
        --primary: #111111; 
        --ink-900: #111111;
        --ink-600: #4b5563;
        --ink-400: #9ca3af;
        --ink-100: #f5f5f5;
        --border: #eeeeee;
        --bg: #fafafa;
        --surface: #ffffff;
    }

    * { box-sizing: border-box; -webkit-font-smoothing: antialiased; margin: 0; padding: 0; }

    .booking-page-root {
        min-height: 100vh;
        background-color: var(--bg);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
        font-family: 'Inter', system-ui, sans-serif;
        color: var(--ink-900);
        padding: 60px 20px;
    }

    .portal-container {
        max-width: 900px;
        width: 100%;
        min-height: 600px;
        display: flex;
        background: var(--surface);
        border: 1px solid var(--border);
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.05), 0 8px 10px -6px rgba(0, 0, 0, 0.05);
    }

    /* Sidebar */
    .brand-sidebar {
        width: 280px;
        background: #fafafa;
        border-right: 1px solid var(--border);
        padding: 40px;
        display: flex;
        flex-direction: column;
        flex-shrink: 0;
    }

    .brand-header { height: 40px; margin-bottom: 40px; display: flex; align-items: center; }
    .brand-logo { max-height: 32px; width: auto; }
    .brand-name-text { font-size: 11px; font-weight: 700; color: var(--ink-400); text-transform: uppercase; letter-spacing: 0.1em; }
    
    .sidebar-profile { margin-bottom: 32px; }
    .avatar-container { width: 56px; height: 56px; border-radius: 50%; overflow: hidden; margin-bottom: 20px; border: 2px solid #fff; box-shadow: 0 2px 4px rgba(0,0,0,0.05); }
    .avatar-img { width: 100%; height: 100%; object-fit: cover; }

    .profile-name { font-size: 18px; font-weight: 700; color: var(--ink-900); letter-spacing: -0.02em; }
    .profile-company { color: var(--ink-600); font-size: 13px; margin: 4px 0 16px; font-weight: 500; }
    .profile-bio { font-size: 13px; color: var(--ink-600); line-height: 1.6; margin-bottom: 32px; }

    .sidebar-info-list { display: flex; flex-direction: column; gap: 14px; padding-top: 32px; border-top: 1px solid var(--ink-100); }
    .sidebar-info-item { display: flex; align-items: center; gap: 12px; color: var(--ink-600); font-size: 12px; font-weight: 500; }
    .sidebar-info-item svg { width: 16px; color: var(--ink-400); }

    .sidebar-social { display: flex; gap: 16px; margin-top: auto; padding-top: 32px; }
    .social-icon { color: var(--ink-400); cursor: pointer; transition: all 0.2s; }
    .social-icon:hover { color: var(--primary); transform: translateY(-2px); }

    /* Right Content */
    .scheduling-content { flex: 1; display: flex; flex-direction: column; background: #fff; position: relative; height: 100%; }

    .content-header { padding: 32px 48px 16px; }
    .main-title { font-size: 20px; font-weight: 700; margin: 0; letter-spacing: -0.03em; color: var(--ink-900); }
    .main-subtitle { font-size: 13px; color: var(--ink-600); margin-top: 4px; }

    .scheduling-body { 
        flex: 1; 
        display: flex; 
        flex-direction: column; 
        padding: 0 48px; 
        overflow: hidden; /* We want internal sections to handle scroll if needed */
    }

    /* Calendar Section - Now Smaller */
    .calendar-section { 
        padding: 20px 0; 
        border-bottom: 1px solid var(--ink-100);
        flex-shrink: 0;
    }
    .section-title { font-size: 10px; font-weight: 700; color: var(--ink-400); text-transform: uppercase; letter-spacing: 0.1em; margin-bottom: 16px; display: block; }
    
    .card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
    .current-month { font-weight: 600; font-size: 14px; color: var(--ink-900); }
    
    .nav-btn {
        width: 30px; height: 30px; border: 1px solid var(--border); background: #fff;
        border-radius: 6px; cursor: pointer; display: flex; align-items: center; justify-content: center;
        transition: all 0.2s; color: var(--ink-600);
    }
    .nav-btn:hover { border-color: var(--primary); color: var(--primary); background: var(--bg); }

    .dow-grid { display: grid; grid-template-columns: repeat(7, 1fr); margin-bottom: 8px; }
    .dow-label { text-align: center; font-size: 10px; font-weight: 700; color: var(--ink-400); text-transform: uppercase; }

    .days-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 3px; }
    .calendar-day {
        height: 38px; /* Slightly bigger than 32px */
        width: 100%;
        display: flex; align-items: center; justify-content: center;
        font-size: 13px; font-weight: 500; border-radius: 6px; cursor: pointer;
        transition: all 0.2s;
    }
    .calendar-day:hover:not(.past):not(.empty) { background: var(--ink-100); color: var(--primary); }
    .calendar-day.selected { background: var(--primary); color: #fff; font-weight: 600; box-shadow: 0 4px 12px -2px rgba(0,0,0,0.1); }
    .calendar-day.past { color: #d1d5db; cursor: not-allowed; }
    .calendar-day.empty { cursor: default; }

    /* Time Selection - Now Scrollable */
    .time-section { 
        padding: 24px 0; 
        flex: 1; 
        display: flex; 
        flex-direction: column; 
        min-height: 0; /* Important for flex-basis/overflow */
        overflow: hidden;
    }
    
    .slots-grid-container {
        flex: 1;
        overflow-y: auto;
        display: grid; 
        grid-template-columns: repeat(auto-fill, minmax(75px, 1fr)); 
        gap: 6px;
        padding-right: 4px;
    }
    .time-slot {
        background: #fff; padding: 8px 4px; text-align: center; border: 1px solid var(--border); border-radius: 6px;
        font-weight: 600; font-size: 11px; cursor: pointer; transition: all 0.2s; color: var(--ink-900);
    }
    .time-slot:hover:not(.busy):not(.selected) { border-color: var(--primary); color: var(--primary); background: var(--bg); }
    .time-slot.selected { background: var(--primary); color: #fff; border-color: var(--primary); box-shadow: 0 4px 12px -2px rgba(0,0,0,0.1); }
    .time-slot.busy { color: #d1d5db; border-color: var(--ink-100); background: #fdfdfd; cursor: not-allowed; text-decoration: line-through; }

    /* Actions */
    .footer-action { padding: 32px 48px; border-top: 1px solid var(--ink-100); background: #fff; }
    .confirm-btn, .solid-btn {
        width: 100%; padding: 14px; background: var(--primary); color: #fff;
        border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer;
        transition: all 0.2s; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }
    .confirm-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); filter: brightness(1.1); }
    .confirm-btn:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }

    .selection-hint { font-size: 13px; color: var(--ink-400); text-align: center; font-weight: 500; }

    /* Status Screen */
    .status-screen { min-height: 100vh; background: var(--bg); display: flex; align-items: center; justify-content: center; padding: 40px; }
    .portal-message-card {
        max-width: 480px; width: 100%; background: #fff; border: 1px solid var(--border); padding: 56px; border-radius: 12px;
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1); text-align: center;
    }
    .status-tag { display: inline-block; padding: 6px 12px; font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; border-radius: 6px; margin-bottom: 24px; }
    .status-tag.success { background: #d1fae5; color: #065f46; }
    .status-tag.error { background: #fee2e2; color: #991b1b; }

    h1 { font-size: 26px; font-weight: 700; margin: 0 0 16px; letter-spacing: -0.04em; color: var(--ink-900); }
    .description { font-size: 15px; color: var(--ink-600); line-height: 1.6; margin-bottom: 32px; }

    .confirmation-meta { background: var(--bg); border: 1px solid var(--border); padding: 24px; border-radius: 8px; margin-bottom: 32px; text-align: left; }
    .meta-row { display: flex; justify-content: space-between; padding: 10px 0; border-bottom: 1px solid var(--border); }
    .meta-row:last-child { border: none; padding-bottom: 0; }
    .meta-row:first-child { padding-top: 0; }
    .meta-row .label { font-size: 12px; font-weight: 600; color: var(--ink-400); text-transform: uppercase; }
    .meta-row .value { font-size: 13px; font-weight: 700; color: var(--ink-900); }

    .success-icon-wrapper {
        width: 64px; height: 64px; border-radius: 50%;
        display: flex; align-items: center; justify-content: center;
        margin: 0 auto 24px;
    }
    .company-logo-success { max-height: 40px; width: auto; display: block; margin: 0 auto 24px; }
    .subtitle { font-size: 15px; color: var(--ink-600); margin-bottom: 24px; }
    .success-notice { font-size: 14px; color: var(--ink-600); line-height: 1.6; margin-bottom: 32px; background: #eff6ff; padding: 16px; border-radius: 8px; border: 1px solid #dbeafe; }

    /* Loading Bar */
    .portal-loading { text-align: center; width: 240px; }
    .loading-bar { width: 100%; height: 4px; background: var(--ink-100); margin-bottom: 20px; border-radius: 10px; position: relative; overflow: hidden; }
    .loading-bar::after {
        content: ''; position: absolute; left: -100%; width: 50%; height: 100%; background: var(--primary);
        animation: progress 1.5s infinite ease-in-out; border-radius: 10px;
    }
    @keyframes progress { 100% { left: 100%; } }
    .portal-loading p { font-size: 12px; font-weight: 600; color: var(--ink-400); margin: 0; letter-spacing: 0.05em; }

    .page-footer { margin-top: 32px; font-size: 12px; color: var(--ink-400); font-weight: 500; }

    /* Animations */
    .animate-up { animation: slideUp 0.4s ease-out; }
    @keyframes slideUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }

    /* Scrollbar Styling */
    .scheduling-body::-webkit-scrollbar { width: 6px; }
    .scheduling-body::-webkit-scrollbar-track { background: transparent; }
    .scheduling-body::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 10px; }
    .scheduling-body::-webkit-scrollbar-thumb:hover { background: #d1d5db; }

    @media (max-width: 800px) {
        .portal-container { flex-direction: column; min-height: auto; }
        .brand-sidebar { width: 100%; border-right: none; border-bottom: 1px solid var(--border); padding: 32px; }
        .scheduling-content { width: 100%; }
        .content-header { padding: 32px; }
        .scheduling-body { padding: 0 32px 32px; }
        .footer-action { padding: 24px 32px; }
    }
`;
